import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import {
  Activity,
  CreditCard,
  LayoutDashboard,
  BookMarked,
  CalendarClock,
  ShieldCheck,
  ShieldAlert,
  MessageSquareHeart,
  BrainCircuit,
  Tags,
  School,
  Users,
  Target,
  LogOut,
  Bell,
  Contact,
  Sparkles,
  Workflow,
  UserCircle2,
  Clapperboard,
  Youtube,
  Megaphone,
  Radio,
  PenLine,
  Mail,
  MailPlus,
  GitBranch,
  HelpCircle,
  Swords,
  Wand2,
  Inbox,
  FolderOpen,
  MessageCircle,
  Layers,
  CalendarRange,
  Menu,
  ChevronDown,
  Wrench,
  Film,
  Music,
  KeyRound,
  Mic,
  Smartphone,
} from 'lucide-react';
import { cn, CompassLogo } from '@jeraisonne/ui';
import { useAuth, isAdmin } from '@/lib/auth';
import { supabase } from '@/lib/supabase';
import { NavBadge } from '@/components/nav-badge';
import { useFeedUnreadCount } from '@/lib/news/use-role-news-feed';
import { AvatarCircle } from '@/components/avatar-circle';
import { useDiscussions } from '@/lib/discussions/queries';
import { countUnreadDiscussions } from '@/lib/discussions/discussion-unread';
import { useTutorSubmissions } from '@/lib/exercise-submissions/api';
import { SPEAKER_PAGE, useCanRecordVoices } from '@/lib/animations/speaker';

/** Nouveautés du Feed depuis sa dernière ouverture (0 sur la page Feed). */
function useTutorFeedBadge(location: string): number {
  const count = useFeedUnreadCount('tutor');
  return location.startsWith('/tutor/news') ? 0 : count;
}

type TutorNavItem = {
  id: string;
  label: string;
  /** Libellé court de la barre du bas (5 onglets sur 375 px, sans troncature). */
  short?: string;
  icon: typeof LayoutDashboard;
  to: string;
  /** Préfixes de chemins qui rendent l'entrée active (défaut : `to`). */
  match?: readonly string[];
  /** Préfixes qui ne la rendent PAS active (une sous-page qui a sa propre entrée). */
  exclude?: readonly string[];
};

/**
 * Menu latéral (desktop), par fréquence d'usage : le quotidien d'abord
 * (accueil, agenda, élèves, messages, copies), puis les contenus. Les
 * « Paramètres » ont fusionné dans « Mon profil » (même clé API, même
 * déconnexion) : `/tutor/settings` y redirige.
 */
export const TUTOR_NAV_ITEMS: readonly TutorNavItem[] = [
  {
    id: 'dashboard',
    label: 'Accueil',
    short: 'Accueil',
    icon: LayoutDashboard,
    to: '/tutor/dashboard',
  },
  {
    id: 'booking',
    label: 'Rendez-vous',
    short: 'Agenda',
    icon: CalendarClock,
    to: '/tutor/rendez-vous',
  },
  {
    id: 'contacts',
    label: 'Mon réseau',
    short: 'Réseau',
    icon: Contact,
    to: '/tutor/contacts',
    match: ['/tutor/contacts', '/tutor/students', '/tutor/classes'],
  },
  { id: 'discussions', label: 'Discussions', icon: MessageCircle, to: '/tutor/discussions' },
  { id: 'submissions', label: 'Exercices reçus', icon: Inbox, to: '/tutor/submissions' },
  { id: 'news', label: 'Feed', short: 'Feed', icon: Bell, to: '/tutor/news' },
];

export const TUTOR_CONTENT_NAV_ITEMS: readonly TutorNavItem[] = [
  {
    id: 'library',
    label: 'Bibliothèque',
    icon: BookMarked,
    to: '/tutor/exercises',
    match: ['/tutor/exercises', '/tutor/epreuves', '/tutor/themes'],
  },
  { id: 'cards', label: 'SKILLCARDS', icon: Layers, to: '/tutor/cards' },
  { id: 'blueprints', label: 'Générateurs d’exercices', icon: Wand2, to: '/tutor/blueprints' },
  { id: 'blog', label: 'Blog réflexif', icon: PenLine, to: '/tutor/blog' },
  { id: 'files', label: 'Mes fichiers', icon: FolderOpen, to: '/tutor/files' },
];

/** Barre du bas (mobile) : 4 destinations quotidiennes + « Menu » (tout le reste). */
export const TUTOR_MOBILE_NAV_ITEMS: readonly TutorNavItem[] = TUTOR_NAV_ITEMS.filter((i) =>
  ['dashboard', 'booking', 'contacts', 'news'].includes(i.id),
);

/** Abonnement Prof (migration 0691) : profs seulement, jamais les admins. */
export const TUTOR_SUBSCRIPTION_NAV_ITEM: TutorNavItem = {
  id: 'subscription',
  label: 'Abonnement Prof',
  icon: CreditCard,
  to: '/tutor/abonnement',
};

/** Entrées du menu mobile (tout ce que la barre du bas ne montre pas). */
/** Voix des animations : comptes « speaker » (et admins), migration 0678. */
export const SPEAKER_NAV_ITEM: TutorNavItem = {
  id: 'speaker',
  label: 'Audios animations',
  icon: Mic,
  to: SPEAKER_PAGE,
};

export const TUTOR_MENU_ITEMS: readonly TutorNavItem[] = [
  ...TUTOR_NAV_ITEMS.filter((i) => !TUTOR_MOBILE_NAV_ITEMS.includes(i)),
  ...TUTOR_CONTENT_NAV_ITEMS,
];

export function isTutorNavActive(item: TutorNavItem, pathname: string): boolean {
  if (item.exclude?.some((prefix) => pathname.startsWith(prefix))) return false;
  return (item.match ?? [item.to]).some((prefix) => pathname.startsWith(prefix));
}

export const TUTOR_ADMIN_NAV_ITEMS: readonly TutorNavItem[] = [
  {
    id: 'admin-chapters',
    label: 'Chapitres & cours',
    icon: ShieldCheck,
    to: '/tutor/admin/chapters',
  },
  { id: 'admin-objectives', label: 'Objectifs', icon: Target, to: '/tutor/admin/objectives' },
  {
    id: 'admin-packs',
    label: 'Packs (progression)',
    icon: CalendarRange,
    to: '/tutor/admin/packs',
  },
  {
    id: 'admin-toolbox',
    label: 'Boîte à outils',
    icon: Wrench,
    to: '/tutor/admin/toolbox',
  },
  {
    id: 'admin-animations',
    label: 'Animations SKILLCARDS',
    icon: Film,
    to: '/tutor/admin/animations',
    exclude: ['/tutor/admin/animations/sons'],
  },
  {
    id: 'admin-animation-sounds',
    label: 'Sons des animations',
    icon: Music,
    to: '/tutor/admin/animations/sons',
  },
  {
    id: 'admin-stats',
    label: 'Statistiques en direct',
    icon: Activity,
    to: '/tutor/admin/stats',
  },
  { id: 'admin-users', label: 'Utilisateurs', icon: Users, to: '/tutor/admin/users' },
  {
    id: 'admin-schools',
    label: 'Établissements',
    icon: School,
    to: '/tutor/admin/etablissements',
  },
  { id: 'admin-faq', label: 'FAQ', icon: HelpCircle, to: '/tutor/admin/faq' },
  {
    id: 'admin-skillcards-pricing',
    label: 'Tarifs et promos',
    icon: Tags,
    to: '/tutor/admin/skillcards-pricing',
  },
  {
    id: 'admin-pipeline',
    label: 'Pipeline exercices',
    icon: Workflow,
    to: '/tutor/admin/pipeline',
  },
  { id: 'admin-prompts', label: 'Prompts IA', icon: BrainCircuit, to: '/tutor/admin/prompts' },
  { id: 'admin-dag', label: 'DAG (backward)', icon: GitBranch, to: '/tutor/admin/dag' },
  {
    id: 'admin-themes-moderation',
    label: 'Modération thèmes',
    icon: Sparkles,
    to: '/tutor/admin/themes/moderation',
  },
  {
    id: 'admin-showcase-playlist',
    label: 'Playlist showcase',
    icon: Clapperboard,
    to: '/tutor/admin/showcase-playlist',
  },
  {
    id: 'admin-hero',
    label: 'Hero de la landing',
    icon: Clapperboard,
    to: '/tutor/admin/hero',
  },
  { id: 'admin-diffusions', label: 'Diffusions', icon: Radio, to: '/tutor/admin/diffusions' },
  { id: 'admin-live', label: 'Live School', icon: Swords, to: '/tutor/admin/live' },
  {
    id: 'admin-connections',
    label: 'Paramètres de connexion',
    icon: KeyRound,
    to: '/tutor/admin/connexions',
  },
  { id: 'admin-youtube', label: 'YouTube Studio', icon: Youtube, to: '/tutor/admin/youtube' },
  {
    id: 'admin-challenges',
    label: 'Diffuser un défi',
    icon: Megaphone,
    to: '/tutor/admin/challenges',
  },
  {
    id: 'admin-blog-moderation',
    label: 'Modération blog',
    icon: PenLine,
    to: '/tutor/admin/blog/moderation',
  },
  {
    id: 'admin-reports',
    label: 'Signalements',
    icon: ShieldAlert,
    to: '/tutor/admin/reports',
  },
  {
    id: 'admin-scratch-feedback',
    label: 'Avis sur le brouillon',
    icon: MessageSquareHeart,
    to: '/tutor/admin/brouillon-avis',
  },
  {
    id: 'admin-android-testers',
    label: 'Testeurs Android',
    icon: Smartphone,
    to: '/tutor/admin/testeurs-android',
  },
  { id: 'admin-newsletter', label: 'Newsletter', icon: Mail, to: '/tutor/admin/newsletter' },
  {
    id: 'admin-alwaysdata',
    label: 'Emails Alwaysdata',
    icon: MailPlus,
    to: '/tutor/admin/alwaysdata',
  },
];

/**
 * Compteurs de la navigation : discussions non lues, exercices reçus en
 * attente, nouveautés du Feed (0 sur sa propre page). Requêtes déjà en cache
 * (mêmes clés que les pages) : aucune requête en plus.
 */
function useTutorNavCounts(location: string) {
  const { user } = useAuth();
  const feed = useTutorFeedBadge(location);
  const { data: discussions = [] } = useDiscussions();
  const { data: submissions = [] } = useTutorSubmissions(user?.id);
  return {
    news: feed,
    discussions: countUnreadDiscussions(discussions, 'tutor'),
    submissions: submissions.length,
  } as Record<string, number>;
}

function useSignOut() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  return useCallback(async () => {
    queryClient.clear();
    await supabase.auth.signOut();
    navigate({ to: '/login' });
  }, [navigate, queryClient]);
}

function SidebarLink({
  item,
  active,
  count,
  tone = 'primary',
}: {
  item: TutorNavItem;
  active: boolean;
  count?: number;
  tone?: 'primary' | 'accent';
}) {
  const Icon = item.icon;
  return (
    <Link
      to={item.to}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'font-display flex min-h-[2.5rem] items-center gap-2.5 rounded-xl px-3 py-2 text-[13px] font-semibold transition-colors',
        active
          ? tone === 'primary'
            ? 'bg-primary-50 text-primary-700'
            : 'bg-accent-50 text-accent-700'
          : 'text-ink-700 hover:bg-ink-100',
      )}
    >
      <Icon
        className={cn(
          'h-[18px] w-[18px] shrink-0',
          active ? (tone === 'primary' ? 'text-primary-600' : 'text-accent-600') : 'text-ink-500',
        )}
      />
      <span className="min-w-0 flex-1 truncate">{item.label}</span>
      {!!count && <NavBadge count={count} />}
    </Link>
  );
}

function GroupTitle({ children }: { children: ReactNode }) {
  return (
    <p className="font-display text-ink-400 mb-1 mt-4 px-3 text-xs font-bold uppercase tracking-widest">
      {children}
    </p>
  );
}

export function TutorSidebar() {
  const { profile, user } = useAuth();
  const admin = isAdmin(profile, user?.email);
  const speaker = useCanRecordVoices();
  const location = useRouterState({ select: (s) => s.location.pathname });
  const handleSignOut = useSignOut();
  const counts = useTutorNavCounts(location);

  if (!profile) return null;

  return (
    <aside className="border-ink-200 native:pt-[env(safe-area-inset-top)] native:pb-[env(safe-area-inset-bottom)] hidden w-60 shrink-0 flex-col border-r bg-white md:flex">
      <div className="shrink-0 px-5 pb-6 pt-5">
        <CompassLogo size="md" subtitle="Espace prof" animated />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto pb-2">
        <nav aria-label="Espace prof" className="flex flex-col gap-0.5 px-3">
          {TUTOR_NAV_ITEMS.map((item) => (
            <SidebarLink
              key={item.id}
              item={item}
              active={isTutorNavActive(item, location)}
              count={counts[item.id]}
            />
          ))}
          <GroupTitle>Contenus</GroupTitle>
          {TUTOR_CONTENT_NAV_ITEMS.map((item) => (
            <SidebarLink key={item.id} item={item} active={isTutorNavActive(item, location)} />
          ))}
          {speaker && (
            <SidebarLink
              item={SPEAKER_NAV_ITEM}
              active={isTutorNavActive(SPEAKER_NAV_ITEM, location)}
            />
          )}
          {!admin && profile.role === 'tutor' && (
            <SidebarLink
              item={TUTOR_SUBSCRIPTION_NAV_ITEM}
              active={isTutorNavActive(TUTOR_SUBSCRIPTION_NAV_ITEM, location)}
            />
          )}
          {admin && (
            <>
              <GroupTitle>Admin</GroupTitle>
              {TUTOR_ADMIN_NAV_ITEMS.map((item) => (
                <SidebarLink
                  key={item.id}
                  item={item}
                  tone="accent"
                  active={isTutorNavActive(item, location)}
                />
              ))}
            </>
          )}
        </nav>
      </div>

      <div className="bg-ink-100 mx-3 mb-4 mt-3 shrink-0 rounded-xl p-3.5">
        <p className="font-display text-ink-500 mb-1 text-xs font-bold uppercase tracking-widest">
          Connecté en tant que
        </p>
        <p className="font-display text-ink-900 truncate text-[14px] font-extrabold leading-tight">
          {profile.display_name ?? 'Professeur'}
        </p>
        <div className="mt-1.5 flex items-center justify-between">
          <Link to="/profile" className="text-primary-600 font-sans text-xs hover:underline">
            Mon profil
          </Link>
          <button
            onClick={handleSignOut}
            className="text-ink-500 hover:text-accent-600 flex items-center gap-1 font-sans text-xs transition-colors"
          >
            <LogOut className="h-3 w-3" />
            Déconnexion
          </button>
        </div>
      </div>
    </aside>
  );
}

export function TutorBottomNav() {
  const location = useRouterState({ select: (s) => s.location.pathname });
  const counts = useTutorNavCounts(location);
  const [menuOpen, setMenuOpen] = useState(false);

  // Le menu se referme dès qu'on change de page.
  useEffect(() => setMenuOpen(false), [location]);

  // Hauteur de la barre publiée dans `--tutor-nav-h` : une barre d'actions
  // fixée en bas d'une page (éditeur de blog…) se pose AU-DESSUS du menu au
  // lieu de passer dessous. 0 quand le menu est masqué (ordinateur).
  const [navEl, setNavEl] = useState<HTMLElement | null>(null);
  useEffect(() => {
    const root = document.documentElement;
    if (!navEl || typeof ResizeObserver === 'undefined') return;
    const apply = () => root.style.setProperty('--tutor-nav-h', `${navEl.offsetHeight}px`);
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(navEl);
    return () => {
      ro.disconnect();
      root.style.removeProperty('--tutor-nav-h');
    };
  }, [navEl]);

  // Pas de bottom nav pendant la prévisualisation d'un exercice : on veut
  // que le tuteur voie le player tel que l'élève, sans chrome de navigation
  // qui chevauche le bouton « Suivant → » flottant.
  if (/^\/tutor\/exercises\/[^/]+\/test\/?$/.test(location)) return null;

  const menuCount = (counts.discussions ?? 0) + (counts.submissions ?? 0);
  const menuActive =
    menuOpen ||
    location.startsWith('/profile') ||
    location.startsWith('/tutor/admin') ||
    TUTOR_MENU_ITEMS.some((i) => isTutorNavActive(i, location));
  const tab = (active: boolean) =>
    cn(
      'font-display flex min-h-[3.25rem] flex-1 flex-col items-center gap-1 pb-2 pt-2.5 text-xs font-semibold transition-colors',
      active ? 'text-primary-600' : 'text-ink-400',
    );

  return (
    <>
      <nav
        ref={setNavEl}
        aria-label="Navigation principale"
        className="border-ink-100 native:pb-[min(env(safe-area-inset-bottom),2.125rem)] ocean:bg-[#061433] ocean:shadow-[0_-4px_20px_rgba(0,0,0,0.35)] fixed inset-x-0 bottom-0 z-20 flex border-t bg-white pb-[env(safe-area-inset-bottom)] shadow-[0_-4px_20px_rgba(5,102,242,0.08)] md:hidden"
      >
        {TUTOR_MOBILE_NAV_ITEMS.map((item) => {
          const Icon = item.icon;
          const active = !menuOpen && isTutorNavActive(item, location);
          return (
            <Link
              key={item.id}
              to={item.to}
              aria-current={active ? 'page' : undefined}
              className={tab(active)}
            >
              <span className="relative">
                <Icon className="h-5 w-5" />
                {!!counts[item.id] && <NavBadge count={counts[item.id]!} variant="icon" />}
              </span>
              <span className="leading-tight">{item.short ?? item.label}</span>
            </Link>
          );
        })}
        <button
          type="button"
          onClick={() => setMenuOpen((o) => !o)}
          aria-expanded={menuOpen}
          aria-controls="tutor-menu"
          className={tab(menuActive)}
        >
          <span className="relative">
            <Menu className="h-5 w-5" />
            {menuCount > 0 && <NavBadge count={menuCount} variant="icon" />}
          </span>
          <span className="leading-tight">Menu</span>
        </button>
      </nav>
      {menuOpen && <TutorMenuSheet counts={counts} onClose={() => setMenuOpen(false)} />}
    </>
  );
}

/**
 * Menu mobile : tout ce qui n'a pas sa place dans la barre du bas (messages,
 * copies, contenus, profil, admin). Feuille posée au-dessus de la barre.
 */
function TutorMenuSheet({
  counts,
  onClose,
}: {
  counts: Record<string, number>;
  onClose: () => void;
}) {
  const { profile, user } = useAuth();
  const admin = isAdmin(profile, user?.email);
  const speaker = useCanRecordVoices();
  const location = useRouterState({ select: (s) => s.location.pathname });
  const handleSignOut = useSignOut();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const row = (item: TutorNavItem, count?: number, tone: 'primary' | 'accent' = 'primary') => {
    const Icon = item.icon;
    const active = isTutorNavActive(item, location);
    return (
      <li key={item.id}>
        <Link
          to={item.to}
          onClick={onClose}
          className={cn(
            'flex min-h-[2.75rem] items-center gap-3 rounded-xl px-3 py-2 font-sans text-sm font-semibold active:scale-[0.98]',
            active ? 'bg-primary-50 text-primary-700' : 'text-ink-800 hover:bg-ink-50',
          )}
        >
          <Icon
            className={cn(
              'h-5 w-5 shrink-0',
              tone === 'accent' ? 'text-accent-600' : 'text-ink-500',
            )}
          />
          <span className="min-w-0 flex-1">{item.label}</span>
          {!!count && <NavBadge count={count} />}
        </Link>
      </li>
    );
  };

  return (
    <div className="fixed inset-0 z-10 md:hidden" id="tutor-menu">
      <button
        type="button"
        aria-label="Fermer le menu"
        className="absolute inset-0 bg-black/30"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
        className="ocean:bg-[#061433] absolute inset-x-0 bottom-0 max-h-[85dvh] overflow-y-auto rounded-t-3xl bg-white px-4 pb-[calc(4.5rem+env(safe-area-inset-bottom))] pt-4 shadow-2xl"
      >
        <Link
          to="/profile"
          onClick={onClose}
          className="bg-ink-50 ocean:bg-white/5 mb-3 flex min-h-[3.5rem] items-center gap-3 rounded-2xl p-3 active:scale-[0.98]"
        >
          <AvatarCircle
            name={profile?.display_name ?? 'Prof'}
            src={profile?.avatar_url ?? null}
            size="md"
          />
          <span className="min-w-0 flex-1">
            <span className="text-ink-900 block truncate font-sans text-sm font-bold">
              {profile?.display_name ?? 'Professeur'}
            </span>
            <span className="text-ink-500 block font-sans text-xs">
              Mon profil, fiche publique et réglages
            </span>
          </span>
          <UserCircle2 className="text-ink-400 h-5 w-5 shrink-0" aria-hidden />
        </Link>

        <ul className="space-y-0.5">
          {TUTOR_MENU_ITEMS.filter((i) => TUTOR_NAV_ITEMS.includes(i)).map((i) =>
            row(i, counts[i.id]),
          )}
        </ul>
        <p className="font-display text-ink-400 mb-1 mt-4 px-3 text-xs font-bold uppercase tracking-widest">
          Contenus
        </p>
        <ul className="space-y-0.5">
          {TUTOR_CONTENT_NAV_ITEMS.map((i) => row(i))}
          {speaker && row(SPEAKER_NAV_ITEM)}
          {!admin && profile?.role === 'tutor' && row(TUTOR_SUBSCRIPTION_NAV_ITEM)}
        </ul>

        {admin && (
          <details className="group mt-4" open={location.startsWith('/tutor/admin')}>
            <summary className="font-display text-ink-400 flex min-h-[2.75rem] cursor-pointer list-none items-center justify-between px-3 text-xs font-bold uppercase tracking-widest">
              Admin ({TUTOR_ADMIN_NAV_ITEMS.length})
              <ChevronDown
                className="h-4 w-4 transition-transform group-open:rotate-180"
                aria-hidden
              />
            </summary>
            <ul className="space-y-0.5">
              {TUTOR_ADMIN_NAV_ITEMS.map((i) => row(i, undefined, 'accent'))}
            </ul>
          </details>
        )}

        <button
          type="button"
          onClick={handleSignOut}
          className="text-ink-500 hover:text-accent-600 mt-4 flex min-h-[2.75rem] w-full items-center justify-center gap-2 font-sans text-sm font-semibold"
        >
          <LogOut className="h-4 w-4" aria-hidden />
          Se déconnecter
        </button>
      </div>
    </div>
  );
}
